// VetCheckupTypesPage.js
// CRUD page for vet_checkup_types table.
// - API base: process.env.REACT_APP_API_URL
// - Endpoints (adjust if needed):
//   GET    /checkup/types
//   POST   /checkup/types
//   PUT    /checkup/types/:id
//   DELETE /checkup/types/:id
//
// Notes:
// - Comments are in English (as requested).
// - Uses JWT from localStorage.getItem('token').
// - i18n used only for dir switching (optional). You can remove if not needed.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Tooltip,
  Chip,
  MenuItem,
  Stack,
  TableContainer,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';import api from '../../services/api';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
  smartActionButtonSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import SmartTablePagination, { useSmartPagination } from '../../components/SmartTablePagination';
import { formatDateTimeInput } from '../../utils/dateUtils';

const apiUrl = process.env.REACT_APP_API_URL; // e.g., http://192.168.1.251:3360

const SEVERITIES = ['low', 'medium', 'high', 'critical'];

const VetCheckupTypesPage = () => {
  const { t, i18n } = useTranslation();
  const dir = i18n?.language === 'ar' ? 'rtl' : 'ltr';
  const align = i18n?.language === 'ar' ? 'right' : 'left';

  // Data state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // Search & sort
  const [q, setQ] = useState('');
  const [sortField, setSortField] = useState('category_ar');
  const [sortDir, setSortDir] = useState('asc');

  // Create/Edit dialog
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    category_ar: '',
    category_en: '',
    name_ar: '',
    name_en: '',
    severity_default: 'low',
    active: true,
  });

  // Delete dialog
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  // ------- Helpers
  const authHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await api.get(`${apiUrl}/checkup/types/active`, {
        headers: authHeader(),
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setRows(data);
    } catch (e) {
      setErr(e.response?.data?.message || t('checkupTypes.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const createRow = async (payload) => {
    await api.post(`${apiUrl}/checkup/types`, payload, {
      headers: authHeader(),
    });
  };
  const updateRow = async (id, payload) => {
    await api.put(`${apiUrl}/checkup/types/${id}`, payload, {
      headers: authHeader(),
    });
  };
  const deleteRow = async (id) => {
    await api.delete(`${apiUrl}/checkup/types/${id}`, {
      headers: authHeader(),
    });
  };

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // ------- Sort/Search
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };
  const sortArrow = (field) =>
    sortField === field ? (sortDir === 'asc' ? '▲' : '▼') : '';

  const filtered = useMemo(() => {
    const norm = (v) => String(v ?? '').toLowerCase();
    const searched = rows.filter((r) => {
      return (
        norm(r.category_ar).includes(q.toLowerCase()) ||
        norm(r.category_en).includes(q.toLowerCase()) ||
        norm(r.name_ar).includes(q.toLowerCase()) ||
        norm(r.name_en).includes(q.toLowerCase()) ||
        norm(r.severity_default).includes(q.toLowerCase())
      );
    });

    const sortVal = (r) => {
      if (sortField === 'active') return Number(r.active ?? 0);
      return String(r[sortField] ?? '');
    };

    return searched.sort((a, b) => {
      const va = sortVal(a);
      const vb = sortVal(b);
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      return sortDir === 'asc'
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
  }, [rows, q, sortField, sortDir]);

  // ------- UI handlers
  const openCreate = () => {
    setEditing(null);
    setForm({
      category_ar: '',
      category_en: '',
      name_ar: '',
      name_en: '',
      severity_default: 'low',
      active: true,
    });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      category_ar: row.category_ar || '',
      category_en: row.category_en || '',
      name_ar: row.name_ar || '',
      name_en: row.name_en || '',
      severity_default: row.severity_default || 'low',
      active: (row.active ?? 1) === 1 || row.active === true,
    });
    setOpen(true);
  };

  const validateForm = () => {
    // Minimal validation
    if (!form.name_ar.trim() && !form.name_en.trim()) {
      return t('checkupTypes.nameRequired');
    }
    if (!SEVERITIES.includes(form.severity_default)) {
      return t('checkupTypes.invalidSeverity');
    }
    return '';
  };

  const handleSave = async () => {
    setErr('');
    const v = validateForm();
    if (v) {
      setErr(v);
      return;
    }

    const payload = {
      // match DB columns exactly
      category_ar: form.category_ar.trim(),
      category_en: form.category_en.trim(),
      name_ar: form.name_ar.trim(),
      name_en: form.name_en.trim(),
      severity_default: form.severity_default,
      active: form.active ? 1 : 0,
    };

    try {
      if (editing) {
        const id = editing.type_id ?? editing.id;
        await updateRow(id, payload);
      } else {
        await createRow(payload);
      }
      setOpen(false);
      await fetchRows();
    } catch (e) {
      // Unique index on key_name might trigger here
      const msg = e.response?.data?.message;
      if (
        msg?.toLowerCase?.().includes('key_name') ||
        msg?.toLowerCase?.().includes('duplicate')
      ) {
        setErr(t('checkupTypes.duplicateKey'));
      } else {
        setErr(msg || t('checkupTypes.saveError'));
      }
    }
  };

  const askDelete = (row) => {
    setDeleteId(row.type_id ?? row.id);
    setConfirmText('');
    setOpenDelete(true);
  };

  const confirmDelete = async () => {
    if (confirmText.trim().toUpperCase() !== 'DELETE') {
      setErr(t('please_type_delete_to_confirm'));
      return;
    }
    try {
      await deleteRow(deleteId);
      setOpenDelete(false);
      await fetchRows();
    } catch (e) {
      setErr(e.response?.data?.message || t('checkupTypes.deleteError'));
    }
  };
  const {
    page,
    rowsPerPage,
    paginatedItems,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useSmartPagination(filtered, [q, sortField, sortDir]);

  const exportToExcel = async () => {
    const data = rows.map((r) => ({
      ID: r.type_id,
      'التصنيف (عربي)': r.category_ar,
      'Category (EN)': r.category_en,
      'اسم الفحص (عربي)': r.name_ar,
      'Checkup Name (EN)': r.name_en,
      Severity: r.severity_default,
      Active: r.active ?? 0 ? 'Yes' : 'No',
    }));
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Vet Checkup Types');
    const headers = data.length ? Object.keys(data[0]) : [];
    if (headers.length) {
      ws.addRow(headers);
      data.forEach((r) => ws.addRow(headers.map((h) => r[h])));
    }
    const excelBuffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const ts = formatDateTimeInput(new Date()).replace(/[:T]/g, '-');
    saveAs(blob, `vet_checkup_types_${ts}.xlsx`);
  };

  // ------- Render
  return (
    <Box
      className="smart-page-container"
      dir={dir}
      sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}
    >
      <SettingsPageHeader
        title={t('checkupTypes.title', 'Vet checkup types')}
        subtitle={t('checkupTypes.pageHint', 'Manage checkup categories, default severity levels, and active status used by clinic records.')}
        count={filtered.length}
        actions={(
          <>
          <TextField
            size="small"
            placeholder={t('checkupTypes.searchPlaceholder', 'Search by category, name, or key...')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { xs: '100%', sm: 280 } }}
          />
          <Tooltip title={t('export_excel', 'Export Excel')}>
            <IconButton onClick={exportToExcel} sx={{ color: COLORS.green }}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}
          >
            {t('checkupTypes.add', 'Add checkup type')}
          </Button>
          </>
        )}
      />

      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2, borderBottom: `1px solid ${COLORS.line}` }}>
          <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{t('checkupTypes.listTitle', 'Checkup types list')}</Typography>
          <Chip label={`${filtered.length} ${t('records', 'records')}`} sx={{ bgcolor: COLORS.goldSoft, fontWeight: 900 }} />
        </Stack>
        <TableContainer sx={smartTableContainerSx}>
        <Table sx={{ ...smartTableSx, tableLayout: 'fixed', minWidth: 980 }}>
          <TableHead sx={smartTableHeadSx}>
            <TableRow>
              <TableCell align="center" width={56}>#</TableCell>
              <TableCell
                align={align}
                width={170}
                sx={{ cursor: 'pointer' }}
                onClick={() => toggleSort('category_ar')}
              >
                {t('category_ar', 'Category (Arabic)')} {sortArrow('category_ar')}
              </TableCell>
              <TableCell
                align={align}
                width={170}
                sx={{ cursor: 'pointer' }}
                onClick={() => toggleSort('category_en')}
              >
                {t('category_en', 'Category (EN)')} {sortArrow('category_en')}
              </TableCell>

              <TableCell
                align={align}
                width={190}
                sx={{ cursor: 'pointer' }}
                onClick={() => toggleSort('name_ar')}
              >
                {t('checkup_name_ar', 'Checkup Name (Arabic)')} {sortArrow('name_ar')}
              </TableCell>
              <TableCell
                align={align}
                width={190}
                sx={{ cursor: 'pointer' }}
                onClick={() => toggleSort('name_en')}
              >
                {t('checkup_name_en', 'Checkup Name (EN)')} {sortArrow('name_en')}
              </TableCell>
              <TableCell
                align="center"
                width={140}
                sx={{ cursor: 'pointer' }}
                onClick={() => toggleSort('severity_default')}
              >
                {t('severity', 'Severity')} {sortArrow('severity_default')}
              </TableCell>
              <TableCell
                align="center"
                width={90}
                sx={{ cursor: 'pointer' }}
                onClick={() => toggleSort('active')}
              >
                {t('active', 'Active')} {sortArrow('active')}
              </TableCell>
              <TableCell align="center" width={140}>{t('actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!loading &&
              paginatedItems.map((r, idx) => (
                <TableRow key={r.type_id ?? idx} hover>
                  <TableCell align="center">{page * rowsPerPage + idx + 1}</TableCell>
                  <TableCell align={align} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.category_ar || '-'}</TableCell>
                  <TableCell align={align} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.category_en || '-'}</TableCell>

                  <TableCell align={align} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name_ar || '-'}</TableCell>
                  <TableCell align={align} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name_en || '-'}</TableCell>
                  <TableCell align="center">
                    <Chip label={r.severity_default || '-'} size="small" sx={{ bgcolor: r.severity_default === 'critical' ? COLORS.roseSoft : COLORS.cardSoft, fontWeight: 800 }} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={r.active ?? 0 ? t('yes') : t('no')} sx={{ bgcolor: r.active ?? 0 ? COLORS.greenSoft : COLORS.cardSoft, fontWeight: 800 }} />
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={t('edit')}>
                        <IconButton sx={smartActionButtonSx(COLORS.blue, COLORS.blueSoft)} onClick={() => openEdit(r)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('delete')}>
                        <IconButton sx={smartActionButtonSx(COLORS.rose, COLORS.roseSoft)} onClick={() => askDelete(r)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            {loading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={26} sx={{ my: 3 }} />
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {q ? t('no_search_results') : t('checkupTypes.noData')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </TableContainer>
        <SmartTablePagination count={filtered.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
        dir={dir}
      >
        <DialogTitle>
          {editing ? t('checkupTypes.edit') : t('checkupTypes.add')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label={t('category_ar')}
              value={form.category_ar}
              onChange={(e) =>
                setForm({ ...form, category_ar: e.target.value })
              }
            />
            <TextField
              label={t('category_en')}
              value={form.category_en}
              onChange={(e) =>
                setForm({ ...form, category_en: e.target.value })
              }
            />

            <TextField
              label={t('checkup_name_ar')}
              value={form.name_ar}
              onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
            />
            <TextField
              label={t('checkup_name_en')}
              value={form.name_en}
              onChange={(e) => setForm({ ...form, name_en: e.target.value })}
            />
            <TextField
              select
              label={t('severity')}
              value={form.severity_default}
              onChange={(e) =>
                setForm({ ...form, severity_default: e.target.value })
              }
            >
              {SEVERITIES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            <FormControlLabel
              control={
                <Switch
                  checked={form.active}
                  onChange={(e) =>
                    setForm({ ...form, active: e.target.checked })
                  }
                />
              }
              label={t('active')}
              sx={{ justifySelf: dir === 'rtl' ? 'end' : 'start' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button variant="contained" onClick={handleSave}>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)} dir={dir}>
        <DialogTitle>{t('delete_confirmation')}</DialogTitle>
        <DialogContent>
          <Typography>{t('please_type_delete_to_confirm')}</Typography>
          <TextField
            fullWidth
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>{t('cancel')}</Button>
          <Button color="error" onClick={confirmDelete}>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VetCheckupTypesPage;
